import React, { Dispatch, FC, memo, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import {
  NavigationDrawer,
  NavigationDrawerHeader,
  NavigationDrawerItem,
  NavigationDrawerSection,
  Skeleton,
} from '@getgo/chameleon-web-react-wrapper'
import { Box, Grid, makeStyles, Theme } from '@material-ui/core'
import { writeStorage } from '@rehooks/local-storage'
import { PHONES_ROUTE } from '@ucc/unified-admin-common'

import { Divider, ListItemLink, OrganizationContext } from 'components'
import config from 'config'
import { AppLinks } from 'utils'
import { calculateBannerHeight } from 'utils/bannerMargin'
import { HOME_KEY, SIDEBAR_WIDTH } from 'utils/constants'
import useSidebarTitle from '../hooks/useSidebarTitle'

const useStyles = makeStyles((theme: Theme) => ({
  root: (props: { bannerMargin: boolean }) => ({
    boxSizing: 'border-box',
    backgroundColor: tokens.backgroundColorSecondary,
    height: `calc(100vh - ${calculateBannerHeight(props)}px)`,
    display: 'flex',
    paddingBottom: tokens.spacing06,
    width: localStorage.getItem(config.SIDEBAR_WIDTH_KEY) || SIDEBAR_WIDTH,
    flexDirection: 'column',
    outline: 'none',
    zIndex: 1196,
    overflow: 'auto',
    borderRight: `1px solid ${tokens.interactiveColorNeutralDefault}`,
  }),
  sidebarDragger: (props: { bannerMargin: boolean; sidebarPanel }) => ({
    display: props.sidebarPanel?.current ? 'block' : 'none',
    width: 8,
    cursor: 'col-resize',
    position: 'absolute',
    top: 0,
    right: -4,
    bottom: 0,
    zIndex: 1197,
    backgroundColor: 'transparent',
    '&:after': {
      content: "''",
      opacity: 0,
      height: '100%',
      width: 5,
      display: 'block',
      marginLeft: 1,
      background: `0 0 0 2px ${tokens.focusColorHighlightDefault}`,
      transition: theme.transitions.create('opacity', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&:hover': {
      '&:after': {
        background: `0 0 0 2px ${tokens.focusColorHighlightDefault}`,
        opacity: 1,
      },
    },
  }),
}))

interface SidebarProps {
  selectedTab: string
  onSelect: Dispatch<SetStateAction<string>>
  onSelectSubTab: (selectedRoute: string, selectedRouteSubSelection: string) => void
  bannerMargin: boolean
}

let isResizing: boolean | null = null

const Sidebar: FC<SidebarProps> = ({ selectedTab, onSelect, onSelectSubTab, bannerMargin }) => {
  const [ready, setReady] = useState<boolean>()
  const location = useLocation()
  const { url } = useRouteMatch()
  const links = AppLinks()
  const pbx = useContext(OrganizationContext)
  const sidebarPanel = useRef<HTMLElement>(null)
  const classes = useStyles({ bannerMargin, sidebarPanel })

  const SectionDivider = () => (
    <Box width="100%" py={1} mt={0} mb={'-8px'}>
      <Divider />
    </Box>
  )

  useEffect(() => {
    // Setting the selected tab in the small sidebar based on current route
    let match
    const route = location.pathname.split('/').filter((n) => n)

    Object.values(links).forEach((link, i) => {
      if (!match) {
        link.forEach((l) => {
          if (Object.keys(links)[i] === route[1] || l.key === route[1]) {
            match = i
          }
        })
      }
    })

    onSelect(Object.keys(links)[match] || HOME_KEY)

    if (pbx) {
      setReady(true)
    }

    const currentSidebarWidth = localStorage.getItem(config.SIDEBAR_WIDTH_KEY)
    if (!currentSidebarWidth) {
      writeStorage(config.SIDEBAR_WIDTH_KEY, `${SIDEBAR_WIDTH}px`)
    }
    // eslint-disable-next-line
  }, [pbx, location.pathname, links])

  const handleLinkClick = (to: string | undefined) => {
    if (!to) return
    const route = to.split('/').filter((n) => n)
    if (route.length === 1) {
      // Every route has a prefix except this one, so we need this to differentiate it from home
      // links
      if (route[0] === 'business-continuity') {
        onSelectSubTab(PHONES_ROUTE, route[0])
        return
      } else {
        onSelectSubTab(HOME_KEY, route[0])
        return
      }
    }
    onSelectSubTab(route[0], route[1])
  }

  const handleMousedown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    // we will only add listeners when needed, and remove them afterward
    document.addEventListener('mousemove', cbHandleMouseMove)
    document.addEventListener('mouseup', cbHandleMouseUp)
    isResizing = true
  }

  const handleMousemove = (e) => {
    if (!isResizing) return

    const offsetRight = e.clientX - document.body.offsetLeft
    const minWidth = 120
    const maxWidth = 480

    if (offsetRight > minWidth && offsetRight < maxWidth) {
      const curSize = offsetRight - 72 // small sidebar width

      // using a ref instead of state will be way faster
      if (sidebarPanel.current) {
        sidebarPanel.current.style.width = `${curSize}px`
      }
    }
  }

  const handleMouseup = () => {
    if (!isResizing) return
    isResizing = false
    document.removeEventListener('mousemove', cbHandleMouseMove)
    document.removeEventListener('mouseup', cbHandleMouseUp)

    if (sidebarPanel.current) {
      writeStorage(config.SIDEBAR_WIDTH_KEY, sidebarPanel.current.style.width)
    }
  }

  const cbHandleMouseMove = useCallback(handleMousemove, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cbHandleMouseUp = useCallback(handleMouseup, [])
  const sidebarTitle = useSidebarTitle(selectedTab)

  return (
    <Box position="relative">
      <Box className={classes.sidebarDragger} onMouseDown={handleMousedown} />

      {ready && links[selectedTab].length > 1 && (
        <NavigationDrawer
          // @ts-ignore
          ref={sidebarPanel}
          className={classes.root}
          tabIndex={-1}
        >
          <NavigationDrawerHeader id="sidebar-title">
            <Box mb={4}>{sidebarTitle}</Box>
          </NavigationDrawerHeader>

          {links[selectedTab]?.map(
            ({ activeIcon, badge, badgeTooltip, external, header, inactiveIcon, primary, to, htmlId, key }, i) => (
              <Box key={`${key}-${i}`}>
                {header ? (
                  <>
                    <SectionDivider />
                    <NavigationDrawerSection>{header}</NavigationDrawerSection>
                  </>
                ) : key === 'skeleton' ? (
                  <Box height={36} px={4} display="flex" alignItems="center">
                    <Grid container alignItems="center" wrap="nowrap" spacing={3}>
                      <Grid item>
                        <Skeleton size="xxsmall" />
                      </Grid>
                      <Grid item style={{ width: '40%' }}>
                        <Skeleton size="xxsmall" variant="rectangle" />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <ListItemLink
                    activeIcon={activeIcon}
                    badge={badge}
                    badgeTooltip={badgeTooltip}
                    external={external}
                    inactiveIcon={inactiveIcon}
                    urlKey={key}
                    onClick={() => handleLinkClick(to)}
                    primary={primary || ''}
                    to={external ? to : `${url}${to || ''}`}
                    id={htmlId || key ? `${htmlId || key}-navlink` : undefined}
                  />
                )}
              </Box>
            ),
          )}

          <NavigationDrawerItem
            // TODO: temporary fix to avoid the bug:
            // `Error: can't access property "setAttribute", e.menuItems[e.focusIndex] is undefined`
            style={{ display: 'none' }}
          >
            test
          </NavigationDrawerItem>
        </NavigationDrawer>
      )}
    </Box>
  )
}

export default memo(Sidebar)
